import debounce from 'debounce'
import { useBundleStore, useCartStore } from '@/stores'
import { getTemplateData, digestSha256, compact } from '@/utils'
import type { SimpleCustomer, Cart } from '@/types/shopify/cart'

const AMPLITUDE_API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY

export const EventTypes = {
  NAVIGATION: '[Event] Navigation Click',
  LINK: '[Event] Link Click',
  BUTTON: '[Event] Button Click',
  ADD_TO_BUNDLE: '[Event] Add to Bundle',
  ADD_TO_CART: '[Event] Add to Cart',
  POSTSCRIPT: '[Event] Postscript',
  PRODUCT_VIEW: '[Event] Product View',
  EXPERIMENT_ASSIGNED: '[Event] Experiment Assigned',
  QUANTITY_CONTROL: '[Event] Quantity Control',
  SCROLL_DEPTH: '[Event] Scroll Depth',
  RETAIL_SEARCH: '[Event] Retail Search'
} as const

interface PostscriptEventDetail {
  type: 'impression' | 'close' | 'formSubmit'
  popupName: string
  popupId: string
  isReopen?: boolean
  values?: Record<string, unknown>
  hard?: boolean
}

interface ExperimentEvent {
  experienceId: string
  experienceName: string
  variationId: string
  variationName: string
}

interface AmplitudeInstance {
  track: (eventType: string, eventProperties: Record<string, unknown>) => void,
  setUserId: (id: string | undefined) => void
}

let amplitudeInstance: AmplitudeInstance | null = null
let amplitudePromise: Promise<AmplitudeInstance> | null = null

const SCROLL_DEPTHS = [ 1, 25, 50, 90 ]
const trackedScrollDepths = new Set<number>()
const trackedQuantityControls = new Set<string>()

const getUserId = async () => {
  const customer = getTemplateData<SimpleCustomer>('#json-customer')
  if (!customer) {
    return
  }

  return await digestSha256(customer.email)
}

const getAmplitude = async () => {
  if (amplitudeInstance) {
    return amplitudeInstance
  }

  if (!amplitudePromise) {
    const initializeAmplitudeInstance = async () => {
      // const [ amplitude, { sessionReplayPlugin }] = await Promise.all([
      //   import('@amplitude/analytics-browser'),
      //   import('@amplitude/plugin-session-replay-browser')
      // ])
      // amplitude.add(sessionReplayPlugin({ sampleRate: 1 }))
      const amplitude = await import('@amplitude/analytics-browser')
      const userId = await getUserId()
      amplitude.init(AMPLITUDE_API_KEY, {
        autocapture: {
          pageViews: true,
          elementInteractions: false
        },
        defaultTracking: true,
        userId
      })
      amplitude.setUserId(userId)
      amplitudeInstance = amplitude
      return amplitude
    }

    amplitudePromise = initializeAmplitudeInstance()
  }
  return amplitudePromise
}

const initializeAmplitude = async () => {
  try {
    await getAmplitude()
    setupEventListeners()
    setupConvertflowTracking()
  } catch (error) {
    console.error('Failed to initialize Amplitude:', error)
  }
}

let debouncedHandleScrollEvents: () => void

const setupEventListeners = () => {
  document.addEventListener('click', handleClickEvents)
  window.addEventListener('postscriptPopup', (event: Event) => trackPostscriptEvent((event as CustomEvent).detail))

  debouncedHandleScrollEvents = debounce(handleScrollEvents, 250)
  window.addEventListener('scroll', debouncedHandleScrollEvents, {
    passive: true
  })
}

const handleClickEvents = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  const navElement = target.closest('nav *')
  const linkElement = target.closest('a')
  const buttonElement = target.closest('button')

  if (navElement || linkElement) {
    trackLinkClick(linkElement || (navElement as HTMLElement), navElement ? EventTypes.NAVIGATION : EventTypes.LINK)
  } else if (buttonElement) {
    trackButtonClick(buttonElement)
  }
}

const handleScrollEvents = () => {
  const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight
  const scrollPosition = window.scrollY
  const scrollPercentage = Math.round((scrollPosition / scrollHeight) * 100)

  SCROLL_DEPTHS.forEach((depth) => {
    if (scrollPercentage >= depth) {
      trackScrollDepth(depth)
    }
  })
}

const trackScrollDepth = (depth: number) => {
  if (trackedScrollDepths.has(depth)) {
    return
  }

  const { track } = useAmplitude()
  const eventProperties = {
    ...getPageProperties(),
    'Scroll Depth': `${depth}%`
  }

  track(EventTypes.SCROLL_DEPTH, eventProperties)
  trackedScrollDepths.add(depth)
}

const setupConvertflowTracking = () => {
  // add handler
  window._conv_q = window._conv_q || []
  window._conv_q.push({
    what: 'addListener',
    params: {
      event: 'experience.activated',
      handler: (event: Event) => trackExperimentEvent(event as unknown as ExperimentEvent)
    }
  })

  // fire events that occurred before init
  if (window._conv_e?.length) {
    window._conv_e.forEach(i => {
      try {
        trackExperimentEvent(i as ExperimentEvent)
      } catch (e) {
        console.warn(e)
      }
    })
  }
}

const getAbProperties = () => {
  try {
    return {
      'AB Tests': window.__ab.tests.value.join(', ')
    }
  } catch {
    return {}
  }
}

const getPageProperties = () => ({
  'Page Url': window.location.href,
  'Page Path': window.location.pathname
})

const getClickPaths = (element: HTMLElement) => {
  const url = new URL(window.location.href)
  const href = String(element.getAttribute('href'))
  try {
    if (href.startsWith('/')) {
      url.pathname = href
    } else if (href.startsWith('http')) {
      const external = new URL(href)
      return {
        url: external.toString(),
        path: external.pathname
      }
    }
  } catch {
    // nothing
  }
  return {
    url: url.toString(),
    path: url.pathname
  }
}

const getGTMProperties = (element: HTMLElement) => {
  const { dataset } = (element.closest('[data-gtm-action]') || element) as HTMLElement
  const {
    gtmCategory,
    gtmAction,
    gtmLabel
  } = dataset
  return compact({
    'Category': gtmCategory,
    'Action': gtmAction,
    'Label': gtmLabel
  })
}

export const trackLinkClick = async (element: HTMLElement, eventType: string) => {
  const { track } = useAmplitude()
  const { url, path } = getClickPaths(element)
  const eventProperties = {
    'Click Url': url,
    'Click Path': path,
    'From Url': document.referrer,
    'From Path': document.referrer ? new URL(document.referrer).pathname : '',
    ...getGTMProperties(element),
    ...getAbProperties()
  }
  await track(eventType, eventProperties)
}

export const trackButtonClick = async (button: HTMLButtonElement) => {
  const { track } = useAmplitude()
  const eventProperties = {
    ...getPageProperties(),
    'Click Classes': button.className || '',
    'Click Element': button.tagName.toLowerCase(),
    'Click Text': button.textContent?.trim() || '',
    'Click Url': window.location.href,
    ...getGTMProperties(button),
    ...getAbProperties()
  }

  if (button.dataset.gtmCategory === 'quantity-control') {
    const label = eventProperties['Label'] || ''
    if (!trackedQuantityControls.has(label)) {
      await track(EventTypes.QUANTITY_CONTROL, eventProperties)
      trackedQuantityControls.add(label)
    }
  } else {
    await track(EventTypes.BUTTON, eventProperties)
  }
}

export const trackPostscriptEvent = async (detail: PostscriptEventDetail) => {
  const { track } = useAmplitude()
  const { type, popupName, popupId } = detail
  const eventProperties = {
    ...getPageProperties(),
    ...getAbProperties(),
    'Type': type,
    'Popup Name': popupName,
    'Popup Id': popupId
  }
  switch (type) {
    case 'impression':
      Object.assign(eventProperties, { isReopen: detail.isReopen })
      break
    case 'close':
      Object.assign(eventProperties, { hard: detail.hard })
      break
    case 'formSubmit':
      Object.assign(eventProperties, detail.values)
      break
  }
  await track(`${EventTypes.POSTSCRIPT}: ${type}`, eventProperties)
}

export const trackExperimentEvent = async ({ experienceId, experienceName, variationId, variationName }: ExperimentEvent) => {
  const { track } = useAmplitude()
  const eventProperties = {
    ...getPageProperties(),
    ...getAbProperties(),
    'Experiment ID': experienceId,
    'Experiment Name': experienceName,
    'Variation ID': variationId,
    'Variation Name': variationName
  }

  await track(EventTypes.EXPERIMENT_ASSIGNED, eventProperties)
}

export const useAmplitude = () => {
  const cartContext = (cart?: Cart) => {
    const cartStore = useCartStore()
    cart ||= cartStore.cart
    return {
      'Customer ID': cartStore.customer?.id,
      'Is Logged In': cartStore.customer?.id ? true : false,
      'Cart Id': cart?.token ? window.atob(cart.token.split('?')[0]) : null,
      'Cart Content': cartStore.cartItemsAnalyticsData
    }
  }

  const track = async (eventType: string, eventProperties: Record<string, unknown>) => {
    const amplitude = await getAmplitude()
    amplitude?.track(eventType, eventProperties)
  }

  const trackBundle = async (productId?: number | null, title?: string) => {
    try {
      const bundleStore = useBundleStore()
      const bundleContent = bundleStore.bundleItemsAnalyticsData

      const eventProperties = {
        ...getPageProperties(),
        ...getAbProperties(),
        ...cartContext(),
        'Bundle Content': bundleContent
      }

      if (productId) {
        const sku = bundleStore.selectedProducts?.find((item) => item.product?.id === productId)?.product?.sku
        eventProperties['Added Product SKU'] = sku
      } else if (title) {
        eventProperties['Added Variety Pack'] = title
      }

      await track(EventTypes.ADD_TO_BUNDLE, eventProperties)
    } catch (error) {
      console.error('Error tracking bundle event:', error)
    }
  }

  const trackCart = async (cart: Cart) => {
    try {
      const eventProperties = {
        ...getPageProperties(),
        ...getAbProperties(),
        ...cartContext(cart)
      }
      await track(EventTypes.ADD_TO_CART, eventProperties)
    } catch (error) {
      console.error('Error tracking cart event:', error)
    }
  }

  return {
    track,
    trackBundle,
    trackCart
  }
}

initializeAmplitude()
